import { z } from 'zod';
import { RELATIVE_DATE_RANGE_TOKENS } from '../date-ranges.js';
import { currencySchema } from './organization.js';
import { optionalText, paginationQuerySchema, toPatchSchema, uuidSchema } from './common.js';

/**
 * Banking
 * =======
 * Real bank (and cash) accounts the organisation holds — a first-class
 * module distinct from a CRM `Account` — connected to Payments Received and
 * Expenses through an append-only transaction ledger. A `BankAccount` never
 * carries a stored running balance: the current balance is always the
 * opening balance plus the signed sum of its `BankTransaction` rows, so it
 * can never silently drift from the ledger that explains it. Nothing here
 * ever lets a caller set a balance directly — only post a transaction.
 */

export const BANK_ACCOUNT_TYPES = ['CURRENT', 'SAVINGS', 'CASH', 'OTHER'] as const;
export type BankAccountType = (typeof BANK_ACCOUNT_TYPES)[number];

export const BANK_ACCOUNT_STATUSES = ['ACTIVE', 'INACTIVE'] as const;
export type BankAccountStatus = (typeof BANK_ACCOUNT_STATUSES)[number];

export const BANK_ACCOUNT_TYPE_LABELS: Record<BankAccountType, string> = {
  CURRENT: 'Current',
  SAVINGS: 'Savings',
  CASH: 'Cash',
  OTHER: 'Other',
};

export const BANK_TRANSACTION_TYPES = [
  'OPENING',
  'PAYMENT',
  'EXPENSE',
  'TRANSFER',
  'ADJUSTMENT',
  'REFUND',
] as const;
export type BankTransactionType = (typeof BANK_TRANSACTION_TYPES)[number];

export const BANK_TRANSACTION_TYPE_LABELS: Record<BankTransactionType, string> = {
  OPENING: 'Opening balance',
  PAYMENT: 'Payment received',
  EXPENSE: 'Expense',
  TRANSFER: 'Transfer',
  ADJUSTMENT: 'Adjustment',
  REFUND: 'Customer refund',
};

const money = z.coerce.number().max(1e15).min(-1e15);

export const createBankAccountSchema = z.object({
  name: z.string().trim().min(1, 'Required').max(120),
  bankName: z.string().trim().min(1, 'Required').max(120),
  accountNumber: optionalText(64),
  accountType: z.enum(BANK_ACCOUNT_TYPES).default('CURRENT'),
  currency: currencySchema,
  /** The account's fixed starting point. Never editable after creation. */
  openingBalance: z.coerce.number().max(1e15).min(-1e15).default(0),
  openingBalanceDate: z.iso.date(),
  status: z.enum(BANK_ACCOUNT_STATUSES).default('ACTIVE'),
  notes: optionalText(2000),
});
export type CreateBankAccountInput = z.infer<typeof createBankAccountSchema>;

/**
 * Opening balance, its date, and the account's currency are fixed at
 * creation and never patchable here — changing any of them after the fact
 * would silently rewrite the ledger's own starting point, or leave posted
 * transactions in a currency the account no longer claims.
 */
export const updateBankAccountSchema = toPatchSchema(
  createBankAccountSchema.omit({
    openingBalance: true,
    openingBalanceDate: true,
    currency: true,
  }),
);
export type UpdateBankAccountInput = z.infer<typeof updateBankAccountSchema>;

export const BANK_ACCOUNT_SORT_FIELDS = ['name', 'bankName', 'createdAt'] as const;

export const listBankAccountsQuerySchema = paginationQuerySchema.extend({
  sortBy: z.enum(BANK_ACCOUNT_SORT_FIELDS).default('name'),
  status: z.enum(BANK_ACCOUNT_STATUSES).optional(),
  bankName: z.string().trim().max(120).optional(),
  currency: z.string().trim().length(3).optional(),
});
export type ListBankAccountsQuery = z.infer<typeof listBankAccountsQuerySchema>;

export interface BankAccountSummary {
  id: string;
  name: string;
  bankName: string;
  /**
   * Masked (e.g. `XXXX XXXX 1234`) for any caller without `bankAccount:manage`.
   * `null` when the account has no number on file, for anyone.
   */
  accountNumber: string | null;
  accountType: BankAccountType;
  currency: string;
  openingBalance: string;
  openingBalanceDate: string;
  /** `openingBalance` plus the signed sum of every posted transaction — never stored. */
  currentBalance: string;
  status: BankAccountStatus;
  createdAt: string;
  updatedAt: string;
}

export interface BankAccountDetail extends BankAccountSummary {
  notes: string | null;
  totals: {
    totalReceived: string;
    totalExpenses: string;
    /** Cash handed back to customers against credit notes. */
    totalRefunds: string;
    transfersIn: string;
    transfersOut: string;
    adjustments: string;
  };
}

/** The minimal shape a Payment/Expense form's "which account" picker needs — never the account number or balance. */
export interface BankAccountOption {
  id: string;
  name: string;
  bankName: string;
  currency: string;
}

export const BANK_TRANSACTION_SORT_FIELDS = ['occurredAt', 'createdAt'] as const;

export const listBankTransactionsQuerySchema = paginationQuerySchema.extend({
  sortBy: z.enum(BANK_TRANSACTION_SORT_FIELDS).default('occurredAt'),
  bankAccountId: uuidSchema.optional(),
  type: z.enum(BANK_TRANSACTION_TYPES).optional(),
  /** Inclusive, on the date the transaction occurred. */
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
  minAmount: z.coerce.number().optional(),
  maxAmount: z.coerce.number().optional(),
  currency: z.string().trim().length(3).optional(),
});
export type ListBankTransactionsQuery = z.infer<typeof listBankTransactionsQuerySchema>;

export interface BankTransactionReference {
  kind: 'PAYMENT' | 'EXPENSE';
  id: string;
  label: string;
}

export interface BankTransactionSummary {
  id: string;
  bankAccountId: string;
  bankAccountName: string;
  type: BankTransactionType;
  /** Signed: positive is money in, negative is money out. */
  amount: string;
  description: string;
  occurredAt: string;
  reference: BankTransactionReference | null;
  transferId: string | null;
  reversalOfId: string | null;
  /**
   * The account's running balance immediately after this entry, in ledger
   * order. `null` when the list was not scoped to one account — a running
   * balance across several accounts would mix unrelated currencies.
   */
  balanceAfter: string | null;
  createdAt: string;
}

export const createBankTransferSchema = z
  .object({
    fromBankAccountId: uuidSchema,
    toBankAccountId: uuidSchema,
    amount: money.refine((value) => value > 0, 'Enter an amount greater than zero'),
    occurredAt: z.iso.date(),
    description: optionalText(300),
  })
  .refine((data) => data.fromBankAccountId !== data.toBankAccountId, {
    message: 'Choose two different accounts',
    path: ['toBankAccountId'],
  });
export type CreateBankTransferInput = z.infer<typeof createBankTransferSchema>;

export const createBankAdjustmentSchema = z.object({
  bankAccountId: uuidSchema,
  /** Signed: positive increases the balance, negative decreases it. Never zero — that would be no entry at all. */
  amount: money.refine((value) => value !== 0, 'Enter a non-zero amount'),
  occurredAt: z.iso.date(),
  /** Required, unlike other free-text notes: an adjustment with no reason is exactly what the ledger exists to prevent. */
  description: z.string().trim().min(1, 'Explain what this adjustment corrects').max(300),
});
export type CreateBankAdjustmentInput = z.infer<typeof createBankAdjustmentSchema>;

export const bankingReportQuerySchema = z.object({
  bankAccountId: uuidSchema.optional(),
  preset: z.enum(RELATIVE_DATE_RANGE_TOKENS).optional(),
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
  type: z.enum(BANK_TRANSACTION_TYPES).optional(),
  /** Only Expense-linked transactions carry a vertical today — see `BankingReportQuery` doc. */
  vertical: z.string().trim().max(80).optional(),
  currency: z.string().trim().length(3).optional(),
});
export type BankingReportQuery = z.infer<typeof bankingReportQuerySchema>;

export interface BankAccountBalanceRow {
  id: string;
  name: string;
  bankName: string;
  currency: string;
  openingBalance: string;
  moneyIn: string;
  moneyOut: string;
  currentBalance: string;
}

/**
 * The overall Banking dashboard. Grouped by currency rather than summed
 * across them: the app never converts one currency's balance into another's
 * without an explicit, dated FX basis, and this report does not attempt one.
 */
export interface BankingOverallReport {
  generatedAt: string;
  from: string | null;
  to: string | null;
  byCurrency: {
    currency: string;
    accounts: BankAccountBalanceRow[];
    totalCurrentBalance: string;
  }[];
}

/** One bank account's own period report — the figures the per-bank export is built from. */
export interface BankAccountReport {
  bankAccountId: string;
  bankAccountName: string;
  currency: string;
  from: string | null;
  to: string | null;
  /** The balance immediately before `from` (or the account's own opening balance, if `from` is unset). */
  openingBalance: string;
  closingBalance: string;
  moneyIn: string;
  moneyOut: string;
  breakdown: {
    payments: string;
    expenses: string;
    /** Customer refunds against credit notes: money out. */
    refunds: string;
    transfersIn: string;
    transfersOut: string;
    adjustments: string;
  };
}
